import { create } from "zustand";
import { t } from "../i18n/index.js";
import { toast } from "../ui/Toast.js";
import { errorText } from "../base/state.js";
import { useUnits } from "../units/state.js";
import { worldApi } from "./api.js";
import { requestScope, SupersededRequest } from "../api/scope.js";
import type { AttackLogEntry, BossView, CampView, MarchAction, MarchView, ScoutReport, WorldMe, WorldTile, WorldView } from "./types.js";

export const VIEW_RADIUS = 18;
export type WorldPanel = "none" | "marches" | "find" | "bookmarks" | "scouts" | "boss" | "attacks";
export const ALL_KINDS = ["enclave", "node", "pack", "elite", "hive", "boss", "ruin", "camp", "vault", "citadel"] as const;

export interface Selection {
  x: number;
  y: number;
  tile: WorldTile | null;
}

interface WorldStore {
  me: WorldMe | null;
  view: WorldView | null;
  selection: Selection | null;
  revenge: { battleId: string; opponentId: string; expiresAt: number; damagePct: number } | null;
  selectionVersion: number;
  prepareRevenge(battleId: string): Promise<boolean>;
  panel: WorldPanel;
  /** Camera jump request consumed by the scene. */
  jump: { x: number; y: number } | null;
  hidden: Set<string>;
  squadSlot: number;
  selectedMarch: string | null;
  watchedMarch: string | null;
  selectMarch(id: string | null): void;
  camp: CampView | null;
  scouts: ScoutReport[] | null;
  boss: BossView | null;
  attacks: AttackLogEntry[] | null;
  loading: boolean;
  refreshMe(): Promise<void>;
  loadAround(cx: number, cy: number): Promise<void>;
  select(sel: Selection | null): void;
  setPanel(p: WorldPanel): void;
  setJump(j: { x: number; y: number } | null): void;
  toggleKind(kind: string): void;
  setSquadSlot(slot: number): void;
  refreshCamp(x: number, y: number): Promise<void>;
  refreshScouts(): Promise<void>;
  refreshBoss(): Promise<void>;
  refreshAttacks(): Promise<void>;
  march(input: { x: number; y: number; action: MarchAction; choice?: "force" | "hack" }): Promise<MarchView | null>;
  scout(x: number, y: number): Promise<MarchView | null>;
  recall(id: string): Promise<void>;
  applyMarch(m: MarchView): void;
  run<T>(op: () => Promise<T>, okText?: string): Promise<T | null>;
}

export const useWorld = create<WorldStore>((set, get) => ({
  me: null,
  view: null,
  selection: null,
  revenge: null,
  selectionVersion: 0,
  prepareRevenge: async battleId => {
    const generation = requestScope().generation;
    const version = get().selectionVersion + 1;
    set({ selectionVersion: version });
    try {
      const preview = await worldApi.revenge(battleId);
      if (generation !== requestScope().generation || version !== get().selectionVersion) return false;
      if (!preview) { toast.info(t("world.revengeUnavailable")); return false; }
      if (!preview.target.owner) return false;
      set({ revenge: { battleId, opponentId: preview.target.owner.id, expiresAt: preview.expiresAt, damagePct: preview.damagePct }, selection: { x: preview.target.x, y: preview.target.y, tile: preview.target }, jump: { x: preview.target.x, y: preview.target.y }, panel: "none", selectedMarch: null });
      return true;
    } catch (e) { if (generation === requestScope().generation && !(e instanceof SupersededRequest)) toast.error(errorText(e)); return false; }
  },
  panel: "none",
  jump: null,
  hidden: new Set<string>(),
  squadSlot: 1,
  selectedMarch: null,
  watchedMarch: null,
  selectMarch: (selectedMarch) => set(state => ({ selectedMarch, selection: null, panel: "none", revenge: null, selectionVersion: state.selectionVersion + 1 })),
  camp: null,
  scouts: null,
  boss: null,
  attacks: null,
  loading: false,
  refreshMe: async () => {
    const generation = requestScope().generation;
    try {
      const me = await worldApi.me();
      if (generation === requestScope().generation) set({ me });
    } catch {
      /* not logged in yet */
    }
  },
  loadAround: async (cx, cy) => {
    if (get().loading) return;
    const generation = requestScope().generation;
    set({ loading: true });
    try {
      const view = await worldApi.view(cx, cy, VIEW_RADIUS);
      if (generation !== requestScope().generation) return;
      set({ view });
      const sel = get().selection;
      if (sel) {
        const fresh = view.tiles.find((t) => t.x === sel.x && t.y === sel.y) ?? null;
        set({ selection: { ...sel, tile: fresh } });
      }
    } catch (e) {
      if (generation === requestScope().generation && !(e instanceof SupersededRequest)) toast.error(errorText(e));
    } finally {
      if (generation === requestScope().generation) set({ loading: false });
    }
  },
  select: (selection) => set(state => ({ selection, camp: null, selectedMarch: null, revenge: null, selectionVersion: state.selectionVersion + 1 })),
  setPanel: (panel) => set({ panel, selectedMarch: null }),
  setJump: (jump) => set({ jump }),
  toggleKind: (kind) => {
    const hidden = new Set(get().hidden);
    if (hidden.has(kind)) hidden.delete(kind);
    else hidden.add(kind);
    set({ hidden });
  },
  setSquadSlot: (squadSlot) => set({ squadSlot }),
  refreshCamp: async (x, y) => {
    const generation = requestScope().generation;
    try {
      const camp = await worldApi.camp(x, y);
      if (generation === requestScope().generation) set({ camp });
    } catch (e) {
      if (generation === requestScope().generation && !(e instanceof SupersededRequest)) toast.error(errorText(e));
    }
  },
  refreshScouts: async () => {
    const generation = requestScope().generation, scouts = await worldApi.scouts().catch(() => []);
    if (generation === requestScope().generation) set({ scouts });
  },
  refreshBoss: async () => {
    const generation = requestScope().generation, boss = await worldApi.boss().catch(() => null);
    if (generation === requestScope().generation) set({ boss });
  },
  refreshAttacks: async () => {
    const generation = requestScope().generation, attacks = await worldApi.attacks().catch(() => []);
    if (generation === requestScope().generation) set({ attacks });
  },
  march: async (input) => {
    const generation = requestScope().generation;
    const slot = get().squadSlot;
    const revenge = get().revenge, selection = get().selection;
    const useRevenge = revenge && input.action === "attack" && selection?.x === input.x && selection.y === input.y && selection.tile?.owner?.id === revenge.opponentId;
    const m = await get().run(() => worldApi.march({ ...input, squadSlot: slot, ...(useRevenge ? { revengeBattleId: revenge.battleId } : {}) }), t("world.toast.marchStarted"));
    if (generation !== requestScope().generation) return null;
    if (m) { get().applyMarch(m); if (useRevenge) set({ revenge: null }); }
    return m;
  },
  scout: async (x, y) => {
    const generation = requestScope().generation;
    const m = await get().run(() => worldApi.scout(x, y), t("world.toast.scoutSent"));
    if (generation !== requestScope().generation) return null;
    if (m) get().applyMarch(m);
    return m;
  },
  recall: async (id) => {
    const generation = requestScope().generation;
    const m = await get().run(() => worldApi.recall(id), t("world.toast.recalled"));
    if (generation !== requestScope().generation) return;
    if (m) get().applyMarch(m);
  },
  applyMarch: (m) => {
    const me = get().me;
    if (!me) return;
    const marches = me.marches.filter((x) => x.id !== m.id);
    if (m.state !== "done") marches.push(m);
    set({ me: { ...me, marches }, ...(m.state === "done" && get().selectedMarch === m.id ? { selectedMarch: null } : {}) });
  },
  run: async (op, okText) => {
    const generation = requestScope().generation;
    try {
      const result = await op();
      if (generation !== requestScope().generation) return null;
      await Promise.all([get().refreshMe(), useUnits.getState().refreshMe(), useUnits.getState().refreshRoster()]);
      if (generation !== requestScope().generation) return null;
      if (okText) toast.success(okText);
      return result;
    } catch (e) {
      if (generation === requestScope().generation && !(e instanceof SupersededRequest)) toast.error(errorText(e));
      return null;
    }
  },
}));

/** Human summary of a finished march for toasts and the march list. */
export function marchOutcomeText(m: MarchView): string {
  const r = m.result;
  if (!r) return "";
  switch (r.outcome) {
    case "gathered":
      return t("world.outcome.gathered", { n: r.amount ?? 0, res: t(`res.${r.resource ?? "RAT"}`) });
    case "won":
      return t("world.outcome.won");
    case "lost":
      return t("world.outcome.lost");
    case "shielded":
      return t("world.outcome.shielded");
    case "scouted":
      return t("world.outcome.scouted");
    case "explored":
      return t("world.outcome.explored");
    case "recalled":
      return t("world.outcome.recalled");
    case "no_squad":
      return t("world.outcome.noSquad");
    case "gathering":
      return t("world.outcome.gathering");
    default:
      return r.outcome ?? "";
  }
}
